import { useDisclosure } from "@mantine/hooks";
import { format } from "date-fns";
import Link from "next/link";
import DeleteRoomIcon from "@/delete-room-icon.svg";
import JoinRoomIcon from "@/join-room-icon.svg";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "~/components/ui/accordion";
import { useI18nContext } from "~/i18n/i18n-react";
import useAuthStore from "~/stores/auth";
import { api, type RouterOutputs } from "~/utils/api";
import getLocalizedLanguage from "~/utils/getLocalizedLanguage";
import useDateFnsLocale from "~/utils/use-date-fns-locale";
import { RoomDeletionConfirmationModal } from "../modals/room-deletion-confirmation-modal";
import { Button } from "../ui/button";

type Props = {
	roomId: string;
	room: RouterOutputs["room"]["list"]["rooms"][number];
};

export const RoomCard = ({ roomId, room }: Props) => {
	const { name, language, startTime, endTime } = room;
	const utils = api.useUtils();
	const { LL } = useI18nContext();
	const locale = useDateFnsLocale();
	const user = useAuthStore((state) => state.user);

	const disclosure = useDisclosure();

	const mutation = api.room.end.useMutation({
		onSuccess: () => {
			void utils.room.invalidate();
		},
	});

	const canDeleteRoom = user?.role === "admin" || room.hostId === user?.id;

	return (
		<div className="flex w-full flex-row gap-4 rounded-lg justify-stretch">
			<RoomDeletionConfirmationModal roomId={roomId} disclosure={disclosure} />
			<Accordion type="single" collapsible className="flex-1">
				<AccordionItem value="item-1" className="!border-none">
					<AccordionTrigger>
						<div
							className="overflow-hidden overflow-ellipsis whitespace-nowrap font-bold text-lg capitalize text-left truncate max-w-xs"
							title={name}
						>
							{name}
						</div>
					</AccordionTrigger>
					<AccordionContent className="flex w-full flex-col text-base ">
						<span className="">
							<span className="whitespace-nowrap font-bold text-secondary-foreground">
								{LL.LANGUAGE_COL()}
							</span>{" "}
							<span className="overflow-hidden overflow-ellipsis whitespace-nowrap font-medium">
								{language
									? (getLocalizedLanguage(LL)(language) ?? language)
									: LL.CREATE_ROOM_FORM.AUTO_DETECT()}
							</span>
						</span>
						<span className="">
							<span className="whitespace-nowrap font-bold text-secondary-foreground">
								{LL.START_DATE_TIME()}
							</span>{" "}
							<span className="overflow-hidden overflow-ellipsis whitespace-nowrap font-medium">
								{startTime
									? format(startTime, "PPP h:mm a", {
											locale,
										})
									: LL.CREATE_ROOM_FORM.SCHEDULEDEFAULT()}
							</span>
						</span>
						<span className="">
							<span className="whitespace-nowrap font-bold text-secondary-foreground">
								{LL.END_DATE_TIME()}
							</span>{" "}
							<span className="overflow-hidden overflow-ellipsis whitespace-nowrap font-medium">
								{endTime
									? format(endTime, "PPP h:mm a", {
											locale,
										})
									: LL.CREATE_ROOM_FORM.SCHEDULEDEFAULT()}
							</span>
						</span>
						<span className="">
							<span className="whitespace-nowrap font-bold text-secondary-foreground">
								{LL.CREATED_BY()}
							</span>{" "}
							<span className="overflow-hidden overflow-ellipsis whitespace-nowrap font-medium">
								{room.host.name}
							</span>
						</span>
					</AccordionContent>
				</AccordionItem>
			</Accordion>

			<div className="flex items-center justify-end gap-2 flex-0">
				{canDeleteRoom && (
					<Button
						className="whitespace-nowrap border-destructive/60"
						disabled={mutation.isPending}
						variant={"outline"}
						size="icon"
						title={LL.END_ROOM()}
						onClick={() => disclosure[1].open()}
					>
						<DeleteRoomIcon className="text-destructive" />
					</Button>
				)}
				<Link className="my-auto" href={`/room/${roomId}`}>
					<Button
						id={`room:${roomId}`}
						disabled={mutation.isPending}
						className="whitespace-nowrap"
						size="icon"
						title={LL.JOIN_NOW()}
					>
						<JoinRoomIcon />
					</Button>
				</Link>
			</div>
		</div>
	);
};
